import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Loader2,
  Layers,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TablePagination } from '@/components/TablePagination';

import { API_URL } from '@/lib/api';

const ProductVariants = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editVariant, setEditVariant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    stock: '',
    status: 'active'
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    setPage(1);
  }, [id]);

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${API_URL}/products/${id}`);
      const d = res.data;
      setProduct({
        id: d.id,
        name: d.name,
        product_code: d.productCode,
        category_name: d.categoryName,
        total_stock: d.totalStock ?? 0,
        variants: (d.variants || []).map(v => ({
          id: v.id,
          name: v.name,
          price: v.price,
          stock: v.stock ?? 0,
          status: v.status === true ? 'active' : 'inactive'
        }))
      });
    } catch (error) {
      toast.error('Failed to load product');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (variant = null) => {
    if (variant) {
      setEditVariant(variant);
      setFormData({
        name: variant.name,
        price: variant.price.toString(),
        stock: variant.stock.toString(),
        status: variant.status
      });
    } else {
      setEditVariant(null);
      setFormData({
        name: '',
        price: '',
        stock: '',
        status: 'active'
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditVariant(null);
    setFormData({
      name: '',
      price: '',
      stock: '',
      status: 'active'
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      name: formData.name,
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock),
      status: formData.status
    };

    try {
      if (editVariant) {
        await axios.put(`${API_URL}/variants/${editVariant.id}`, {
          variantName: data.name,
          price: data.price,
          stock: data.stock,
          status: data.status === 'active'
        });
        toast.success('Variant updated successfully');
      } else {
        await axios.post(`${API_URL}/products/${id}/variants`, {
          variantName: data.name,
          price: data.price,
          stock: data.stock
        });
        toast.success('Variant added successfully');
      }
      closeModal();
      fetchProduct();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save variant';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVariant = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/variants/${deleteTarget.id}`);
      toast.success('Variant deleted');
      setDeleteTarget(null);
      fetchProduct();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (variant) => {
    try {
      await axios.put(`${API_URL}/variants/${variant.id}`, {
        status: variant.status !== 'active'
      });
      toast.success('Variant status updated');
      fetchProduct();
    } catch (error) {
      toast.error('Failed to update variant status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
      </div>
    );
  }

  return (
    <div data-testid="product-variants-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/products')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="page-title">Product Variants</h1>
          <p className="text-slate-500 mt-1">
            Managing variants for <strong>{product?.name}</strong>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Product Info */}
        <Card>
          <CardHeader>
            <CardTitle>Product Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-slate-500">Name</p>
              <p className="font-medium">{product?.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Code</p>
              <p className="font-mono text-sm">{product?.product_code}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Category</p>
              <p>{product?.category_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Stock</p>
              <p className={product?.total_stock === 0 ? 'text-rose-600 font-medium' : 'font-medium'}>
                {product?.total_stock}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Variants Table */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Variants</h2>
            <Button 
              onClick={() => openModal()}
              className="bg-purple-950 hover:bg-purple-900"
              data-testid="add-variant-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Variant
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {!product?.variants || product.variants.length === 0 ? (
              <div className="empty-state">
                <Layers className="w-12 h-12 mb-3 text-slate-300" />
                <p className="text-lg font-medium">No variants found</p>
                <p className="text-sm mt-1">Add variants like sizes or quantities</p>
              </div>
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Variant Name</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Stock</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {product.variants.slice((page - 1) * pageSize, page * pageSize).map((variant) => (
                    <TableRow key={variant.id} className="table-row-hover" data-testid={`variant-row-${variant.id}`}>
                      <TableCell className="font-medium">{variant.name}</TableCell>
                      <TableCell>₹{variant.price.toLocaleString()}</TableCell>
                      <TableCell className={variant.stock === 0 ? 'text-rose-600 font-medium' : ''}>
                        {variant.stock}
                      </TableCell>
                      <TableCell>
                        <Badge className={`status-${variant.status}`}>
                          {variant.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openModal(variant)}
                            data-testid={`edit-variant-${variant.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Switch
                            checked={variant.status === 'active'}
                            onCheckedChange={() => handleToggleStatus(variant)}
                            data-testid={`toggle-variant-${variant.id}`}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(variant)}
                            className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                            data-testid={`delete-variant-${variant.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                data={product.variants}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
              />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" /> Delete Variant
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-slate-700">
              Are you sure you want to delete variant <strong>{deleteTarget?.name}</strong>?
            </p>
            <p className="text-xs text-slate-500 mt-1">This will remove the variant and its stock. This cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDeleteVariant} disabled={isDeleting} className="bg-rose-600 hover:bg-rose-700 text-white">
              {isDeleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variant Modal */}
      <Dialog open={modalOpen} onOpenChange={closeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editVariant ? 'Edit Variant' : 'Add Variant'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 py-4">
            <div>
              <Label htmlFor="name" className="form-label">Variant Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., 100ml, 500g, Large"
                required
                data-testid="variant-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price" className="form-label">Price (₹) *</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                  data-testid="variant-price-input"
                />
              </div>
              <div>
                <Label htmlFor="stock" className="form-label">Stock *</Label>
                <Input
                  id="stock"
                  name="stock"
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={handleChange}
                  placeholder="0"
                  required
                  data-testid="variant-stock-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Status</p>
                <p className="text-sm text-slate-500">Enable or disable this variant</p>
              </div>
              <Switch
                checked={formData.status === 'active'}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, status: checked ? 'active' : 'inactive' }))
                }
                data-testid="variant-status-toggle"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-purple-950 hover:bg-purple-900"
                disabled={saving}
                data-testid="save-variant-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editVariant ? 'Update' : 'Add'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductVariants;
